/**
 * FDV — motor de regalos del carrito (Núcleo · C1).
 * Ver docs/carrito-y-regalos/15-carrito-regalos.md.
 *
 * Headless. Una sola instancia, global (se carga desde theme.liquid vía
 * snippets/fdv-gift-engine.liquid, patch #24).
 *
 * Qué hace:
 *   1. Lee las ofertas del <script id="fdv-gift-offers"> (horneado por Liquid
 *      desde el metaobjeto `fdv_gift_offer`).
 *   2. En cada `cartLinesUpdate` (Standard Events de Horizon) reconcilia las
 *      líneas de regalo: agrega las que deben estar, quita las que ya no.
 *   3. Publica el estado en `window.FDV.gifts.state` + evento `fdv:gifts:state`
 *      (contrato B — lo consume la barra de progreso C2 y, a futuro, los modales).
 *   4. Al agregar una línea le pone `properties._gift` / `_gift_label`
 *      (contrato A — lo consume el lock de línea C3, patch #26).
 *   5. Emite eventos CRO por el bus (`window.FDV.track`): fdv_gift_unlocked,
 *      fdv_gift_removed, fdv_gift_added_to_cart, fdv_promo_claimed, fdv_gift_error.
 *
 * NO toca DOM de UI. NO re-fetchea el HTML del carrito (usa el Section
 * Rendering de Horizon vía el CartLinesUpdateEvent).
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const DEBOUNCE_MS = 120;
const MAX_OPS_PER_CYCLE = 6;
const GIFT_PROP = '_gift';
const GIFT_LABEL_PROP = '_gift_label';

const log = (...a) => {
  if (window.FDV?.gifts?.debug) console.log('[fdv-gifts]', ...a);
};
const warn = (...a) => console.warn('[fdv-gifts]', ...a);

/* ------------------------------------------------------------------ config -- */

function readOffers() {
  const el = document.getElementById('fdv-gift-offers');
  if (!el) return [];
  try {
    const raw = JSON.parse(el.textContent || '{}');
    const offers = Array.isArray(raw) ? raw : raw.offers;
    return Array.isArray(offers) ? offers.map(normalizeOffer) : [];
  } catch (e) {
    warn('no se pudo parsear #fdv-gift-offers', e);
    return [];
  }
}

function readBootstrap() {
  const el = document.getElementById('fdv-gift-offers');
  if (!el) return {};
  try {
    const raw = JSON.parse(el.textContent || '{}');
    return raw && !Array.isArray(raw) ? raw.bootstrap || {} : {};
  } catch {
    return {};
  }
}

/** Normaliza tipos (montos a centavos, ids a Number, defaults). */
function normalizeOffer(o) {
  const num = (v) => (v == null || v === '' ? null : Number(v));
  return {
    id: String(o.id ?? o.handle ?? o.internal_name ?? Math.random().toString(36).slice(2)),
    internalName: o.internal_name || o.id || '',
    priority: num(o.priority) ?? 100,
    triggerType: o.trigger_type || 'cart_subtotal',
    thresholdCents: o.threshold_amount != null && o.threshold_amount !== '' ? Math.round(Number(o.threshold_amount) * 100) : null,
    thresholdQty: num(o.threshold_quantity),
    eligibleCollection: o.eligible_collection
      ? { id: num(o.eligible_collection.id), productIds: (o.eligible_collection.product_ids || []).map(Number) }
      : null,
    triggerProductId: num(o.trigger_product_id ?? o.trigger_product?.id),
    // `discount_code` exacto, o `*TEXTO*` = cualquier código que CONTENGA TEXTO (códigos únicos por persona).
    discountCode: /^\*.+\*$/.test((o.discount_code || '').trim()) ? null : (o.discount_code || '').trim().toUpperCase() || null,
    discountCodeContains: /^\*.+\*$/.test((o.discount_code || '').trim())
      ? (o.discount_code || '').trim().slice(1, -1).toUpperCase()
      : null,
    urlParam: (o.url_param || '').trim() || null,
    giftVariantId: num(o.gift_variant?.id ?? o.gift_variant_id),
    giftVariantPrice: num(o.gift_variant?.price),
    giftQty: num(o.gift_quantity) || 1,
    matchTriggerQty: o.match_trigger_quantity === true,
    mechanism: o.mechanism || 'zero_price',
    autoDiscountTitle: (o.auto_discount_title || '').trim().toUpperCase() || null,
    startAt: num(o.start_at), // epoch seconds; Liquid ya filtró, esto es defensa
    endAt: num(o.end_at),
    publicEndAt: num(o.public_end_at), // cierre visible; endAt conserva margen de entrega
    unlockedHeadline: o.unlocked_headline || '',
    progressText: o.progress_text || '',
    rewardLabel: (o.reward_label || '').trim(),
    giftLineLabel: o.gift_line_label || '✨ Producto de regalo',
    stackable: o.stackable !== false,
    mystery: o.mystery === true, // "regalo sorpresa": no se nombra el premio en la barra
    nearPct: num(o.near_pct) ?? 80,
    // Campos de superficie promocional — el motor los ignora; los consume
    // `fdv-offer-state.js` (cerebro de ofertas, doc 16 §3). Passthrough.
    promoSurface: o.promo_surface === true,
    promoHeadline: o.promo_headline || '',
    promoBody: o.promo_body || '',
    promoCtaLabel: o.promo_cta_label || '',
    promoCtaUrl: o.promo_cta_url || '',
    promoImage: o.promo_image || '',
  };
}

/* -------------------------------------------------------------- evaluación -- */

const inCollection = (offer, productId) =>
  !offer.eligibleCollection || offer.eligibleCollection.productIds.includes(Number(productId));

/**
 * Suma en centavos del carrito (opcionalmente acotada a la colección de la oferta).
 *
 * Para el carrito completo se usa `merchSubtotal()` = subtotal de mercancía
 * **después de TODOS los descuentos** (de línea + códigos de nivel carrito) y
 * **sin envío**, que es la base contra la que Shopify evalúa el umbral de la
 * tarifa de envío gratis en el checkout.
 *
 * ⚠️ NO usar `cart.total_price`: si el cliente ya llegó al paso de envío del
 * checkout y regresa al carrito, Shopify le SUMA la tarifa elegida ($139) a
 * `total_price`. Un carrito de $599.50 leía $738.50 → la barra decía "envío
 * gratis desbloqueado" y el checkout cobraba el envío. Tampoco `items_subtotal_price`
 * a secas: es pre-código de descuento de nivel carrito.
 *
 * `cart_level_discount_applications` llega en dos formas: plana en `/cart.js`
 * (`{target_type, total_allocated_amount}`) y anidada en las respuestas de
 * `/cart/add.js` y `/cart/update.js` (`{amount, discount_application: {…}}`).
 * Con la anidada el cupón no se restaba: con BIENVENIDA10 un carrito de $1,151
 * se leía como $1,279 y la escalera marcaba «¡Listo!» el regalo de $1,199 (4-oct-2026).
 */
function merchSubtotal(cart) {
  const items = cart.items || [];
  if (!items.length) return 0;
  const lines = items.reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
  const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, app) => {
    const da = app.discount_application || app;
    if (da.target_type === 'shipping_line') return sum;
    return sum + (app.total_allocated_amount ?? app.amount ?? da.total_allocated_amount ?? 0);
  }, 0);
  return Math.max(0, lines - cartDiscounts);
}

function subtotalFor(offer, cart) {
  if (!offer.eligibleCollection) return merchSubtotal(cart);
  return cart.items
    .filter((it) => inCollection(offer, it.product_id) && !isGiftLine(it))
    .reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
}

function collectionQtyFor(offer, cart) {
  return cart.items
    .filter((it) => inCollection(offer, it.product_id) && !isGiftLine(it))
    .reduce((sum, it) => sum + it.quantity, 0);
}

/** Productos con envío gratis propio (`freeShippingHandles` del bootstrap, snippet
 *  `fdv-free-shipping-products`): con uno en el carrito, el envío ya es gratis. */
let freeShippingHandles = null;
function cartHasFreeShippingProduct(cart) {
  if (freeShippingHandles === null) {
    const list = readBootstrap().freeShippingHandles;
    freeShippingHandles = Array.isArray(list) ? list : [];
  }
  return freeShippingHandles.length > 0 && (cart.items || []).some((it) => freeShippingHandles.includes(it.handle));
}

function triggerProductQty(offer, cart) {
  return cart.items
    .filter((it) => Number(it.product_id) === offer.triggerProductId && !isGiftLine(it))
    .reduce((sum, it) => sum + it.quantity, 0);
}

/** Títulos de los códigos aplicados (descuentos de carrito y de línea). */
function appliedCodeTitles(cart) {
  const titles = [];
  for (const app of cart.cart_level_discount_applications || []) {
    if (app.discount_application?.type === 'discount_code' || app.type === 'discount_code') {
      titles.push((app.title || app.discount_application?.title || '').toUpperCase());
    }
  }
  for (const it of cart.items || []) {
    for (const alloc of it.line_level_discount_allocations || []) {
      if (alloc.discount_application?.type === 'discount_code') {
        titles.push((alloc.discount_application.title || '').toUpperCase());
      }
    }
  }
  return titles;
}

/**
 * Códigos que el cliente trae en el carrito, aplicables o no (`cart.discount_codes`) + los ya aplicados.
 * Un código «compra X, lleva Y» sale `applicable: false` hasta que el regalo está en el carrito,
 * por eso no basta con mirar los descuentos aplicados (prueba del 12-oct-2026).
 */
function cartCodes(cart, onlyValid = false) {
  // `onlyValid`: solo los códigos que Shopify marca `applicable: true` (existen, vigentes y con carrito). Un código inventado sale `false`.
  const codes = (cart.discount_codes || []).filter((c) => !onlyValid || c.applicable === true).map((c) => String(c.code || '').toUpperCase());
  return [...new Set([...codes, ...appliedCodeTitles(cart)])].filter(Boolean);
}

/** Coincide el código con la oferta: exacto o por «contiene» (comodín `*TEXTO*`). */
function codeMatchesOffer(offer, code) {
  if (offer.discountCode) return code === offer.discountCode;
  if (offer.discountCodeContains) return code.includes(offer.discountCodeContains);
  return false;
}

/** ¿Hay un código de la oferta en el carrito? */
function discountCodeApplied(offer, cart) {
  // Desde el 12-oct (noche) la llave ya NO es un descuento de Shopify: el checkout rechazaba el descuento de envío con
  // «no está disponible para tu dirección de envío» y bloqueaba el pago. El código queda en la cookie del carrito
  // (`/discount/<código>`) aunque no exista en Shopify (`applicable: false`) y el tema lo reconoce por el marcador.
  // Barrera suave, decisión del usuario: un código inventado con el marcador también abre el regalo.
  return cartCodes(cart, false).some((c) => codeMatchesOffer(offer, c)) || hasSusurroPass(offer);
}

/**
 * «Pase» del susurro (12-oct, noche): la propia página privada `/pages/la-corte-susurra` es la llave. Al cargarla, `fdv-susurro.js`
 * guarda `fdv_susurro_pass` (localStorage y cookie). Así entran también quienes abrieron una liga sin código (el primer correo, el
 * cambio de navegador). Solo aplica a la oferta por marcador `-INMORTAL-`. Barrera suave, decisión del usuario: la exclusividad
 * es «conocer la dirección» hasta las 00:00 del 15-oct.
 */
function hasSusurroPass(offer) {
  if (!offer.discountCodeContains || !offer.discountCodeContains.includes('INMORTAL')) return false;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('fdv_susurro_pass')) return true;
  } catch {
    /* noop */
  }
  try {
    return typeof document !== 'undefined' && /(?:^|;\s*)fdv_susurro_pass=/.test(document.cookie || '');
  } catch {
    return false;
  }
}

/**
 * Subtotal de mercancía SIN líneas de regalo, después de descuentos de nivel carrito.
 * Es la base del umbral de las ofertas por código: un regalo `auto_discount` entra a precio
 * normal hasta que Shopify aplica el código, y no debe contar para su propio umbral.
 */
function merchSubtotalSinRegalos(cart) {
  const lines = (cart.items || [])
    .filter((it) => !isGiftLine(it))
    .reduce((sum, it) => sum + (it.final_line_price ?? it.line_price ?? 0), 0);
  const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, app) => {
    const da = app.discount_application || app;
    if (da.target_type === 'shipping_line') return sum;
    return sum + (app.total_allocated_amount ?? app.amount ?? da.total_allocated_amount ?? 0);
  }, 0);
  return Math.max(0, lines - cartDiscounts);
}

/**
 * Si un regalo por código no llegó a $0 (código inválido, ya usado, umbral distinto al de Shopify),
 * se retira y se recuerda en la sesión para ese código y ese subtotal. Sin esto el motor lo
 * volvería a agregar en bucle. Si el subtotal o los códigos cambian, se reintenta.
 */
const codeFailKey = (offer) => `fdv_gift_codefail:${offer.id}`;
function codeSignature(offer, cart) {
  return cartCodes(cart).filter((c) => codeMatchesOffer(offer, c)).join('|') + ':' + merchSubtotalSinRegalos(cart);
}
function codeAlreadyFailed(offer, cart) {
  try {
    return sessionStorage.getItem(codeFailKey(offer)) === codeSignature(offer, cart);
  } catch {
    return false;
  }
}
function markCodeFailed(offer, cart) {
  try {
    sessionStorage.setItem(codeFailKey(offer), codeSignature(offer, cart));
  } catch {
    /* noop */
  }
}

const isGiftLine = (item) => !!(item.properties && item.properties[GIFT_PROP]);

/** URL param persistido en sessionStorage mientras dure la pestaña. */
function urlParamActive(offer) {
  if (!offer.urlParam) return false;
  const key = `fdv_gift_url:${offer.urlParam}`;
  try {
    if (new URLSearchParams(location.search).toString().toLowerCase().includes(offer.urlParam.toLowerCase()) ||
        location.href.toLowerCase().includes(offer.urlParam.toLowerCase())) {
      sessionStorage.setItem(key, '1');
    }
    return sessionStorage.getItem(key) === '1';
  } catch {
    return location.href.toLowerCase().includes(offer.urlParam.toLowerCase());
  }
}

function clearUrlParam(offer) {
  if (!offer.urlParam) return;
  try {
    sessionStorage.removeItem(`fdv_gift_url:${offer.urlParam}`);
  } catch {
    /* noop */
  }
}

/**
 * Estado puro por oferta a partir del carrito. Sin efectos secundarios
 * (salvo el sello de sessionStorage para url_param). Testeable.
 */
function offerActive(offer, now = Date.now() / 1000) {
  let preview = false;
  if (offer.id === 'confetti-cosmico-31oct' && offer.startAt > now) {
    const flag = new URLSearchParams(location.search).get('confetti_preview');
    try {
      if (flag === '0') sessionStorage.removeItem('fdv_confetti_preview_until');
      if (flag === '1') sessionStorage.setItem('fdv_confetti_preview_until', String(offer.startAt));
      preview = flag !== '0' && Number(sessionStorage.getItem('fdv_confetti_preview_until')) > now;
    } catch { preview = flag === '1'; }
  }
  return (!offer.startAt || offer.startAt <= now || preview) && (!offer.endAt || offer.endAt > now);
}

function evaluate(offers, cart) {
  const now = Date.now() / 1000;
  const states = offers
    // `promo_only` = oferta que solo alimenta superficies promocionales
    // (`fdv-offer-state.js`); no tiene mecánica de regalo → fuera del motor.
    .filter((o) => o.triggerType !== 'promo_only' && offerActive(o, now))
    .map((offer) => {
      // Oferta por código con umbral: solo es «escalera» (threshold) para quien YA trae el código;
      // para el resto sigue siendo binaria y no se pinta (la Poción del Inmortal no se anuncia).
      const codeWithThreshold = offer.triggerType === 'discount_code' && offer.thresholdCents > 0;
      const codePresent = codeWithThreshold && discountCodeApplied(offer, cart);
      const kind =
        offer.triggerType === 'cart_subtotal' || offer.triggerType === 'collection_quantity' || codePresent
          ? 'threshold'
          : 'binary';
      let progress = 0;
      let remainingAmount = 0;
      let remainingQty = 0;
      let threshold = null;
      let qualifies = false;

      if (offer.triggerType === 'cart_subtotal') {
        threshold = offer.thresholdCents ?? 0;
        const cur = subtotalFor(offer, cart);
        progress = threshold > 0 ? Math.min(1, cur / threshold) : 1;
        remainingAmount = Math.max(0, threshold - cur);
        // Umbral explícito $0: regalo universal, incluso en carrito vacío.
        qualifies = offer.thresholdCents !== null && cur >= threshold;
      } else if (offer.triggerType === 'collection_quantity') {
        threshold = offer.thresholdQty ?? 0;
        const cur = collectionQtyFor(offer, cart);
        progress = threshold > 0 ? Math.min(1, cur / threshold) : 1;
        remainingQty = Math.max(0, threshold - cur);
        qualifies = cur >= threshold && threshold > 0 && (offer.mechanism !== 'zero_price' || merchSubtotal(cart) > 0);
      } else if (offer.triggerType === 'product_in_cart') {
        qualifies = triggerProductQty(offer, cart) > 0;
        progress = qualifies ? 1 : 0;
      } else if (offer.triggerType === 'discount_code') {
        if (codeWithThreshold) {
          // código + umbral de mercancía (sin contar el propio regalo); el código ya lo valida Shopify
          threshold = offer.thresholdCents;
          const cur = codePresent ? merchSubtotalSinRegalos(cart) : 0;
          progress = codePresent ? Math.min(1, cur / threshold) : 0;
          remainingAmount = codePresent ? Math.max(0, threshold - cur) : 0;
          qualifies = codePresent && cur >= threshold && !codeAlreadyFailed(offer, cart);
        } else {
          qualifies = discountCodeApplied(offer, cart);
          progress = qualifies ? 1 : 0;
        }
      } else if (offer.triggerType === 'url_param') {
        qualifies = urlParamActive(offer);
        progress = qualifies ? 1 : 0;
      }

      if (offer.mechanism === 'free_shipping_message' && !qualifies && cartHasFreeShippingProduct(cart)) {
        qualifies = true;
        progress = 1;
        remainingAmount = 0;
      }

      let status = 'locked';
      if (qualifies) status = 'unlocked';
      else if (kind === 'threshold' && progress * 100 >= offer.nearPct) status = 'near';

      const giftQty = offer.matchTriggerQty ? triggerProductQty(offer, cart) || 0 : offer.giftQty;

      return {
        id: offer.id,
        internalName: offer.internalName,
        kind,
        status,
        progress,
        remainingAmount,
        remainingQty,
        threshold,
        giftVariantId: offer.giftVariantId,
        giftQty,
        mechanism: offer.mechanism,
        priority: offer.priority,
        stackable: offer.stackable,
        mystery: offer.mystery,
        unlockedHeadline: offer.unlockedHeadline,
        progressText: renderProgressText(offer.progressText, { remainingAmount, remainingQty, threshold }),
        rewardLabel: offer.rewardLabel,
        giftLineLabel: offer.giftLineLabel,
        autoDiscountTitle: offer.autoDiscountTitle,
        discountApplied: null, // se rellena tras reconciliar (solo auto_discount)
        error: null,
        _offer: offer,
      };
    })
    .sort((a, b) => a.priority - b.priority);

  // Una oferta de monto con `stackable: false` (la Poción del Inmortal, con código o pública) REEMPLAZA a las demás ofertas de monto
  // que comparten su umbral (el regalo de $1,199 anterior): la escalera muestra un solo escalón y no se entregan los dos regalos.
  // No depende de la prioridad (la Poción va con prioridad alta para no bloquear a las cartas del tarot). Si su código falló
  // (inválido, ya usado), deja de reemplazar y el regalo anterior vuelve.
  const reemplazos = states.filter(
    (s) => s.kind === 'threshold' && s.stackable === false && s.threshold > 0 && !(s._offer.triggerType === 'discount_code' && codeAlreadyFailed(s._offer, cart))
  );
  if (!reemplazos.length) return states;
  return states.filter(
    (s) => !reemplazos.some((r) => r !== s && s._offer.triggerType !== 'discount_code' && s.threshold === r.threshold)
  );
}

function renderProgressText(tpl, { remainingAmount, remainingQty, threshold }) {
  if (!tpl) return '';
  return tpl
    .replace(/\{\{\s*restante\s*\}\}/g, formatMoney(remainingAmount))
    .replace(/\{\{\s*restante_cantidad\s*\}\}/g, String(remainingQty))
    .replace(/\{\{\s*meta\s*\}\}/g, threshold != null ? formatMoney(threshold) : '');
}

function formatMoney(cents) {
  const v = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: window.FDV?.gifts?.currency || 'MXN',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    }).format(v);
  } catch {
    return `$${Math.round(v)}`;
  }
}

/* ------------------------------------------------------------- mutaciones -- */

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data = {};
  try {
    data = JSON.parse(text);
  } catch {
    /* algunas respuestas de error no son JSON */
  }
  if (!res.ok) {
    const err = new Error(data.description || data.message || `HTTP ${res.status}`);
    err.data = data;
    throw err;
  }
  return data;
}

/* --------------------------------------------------------------- el motor -- */

class GiftEngine {
  #offers = [];
  #timer = null;
  #running = false;
  #rerunQueued = false;
  #ownEvent = false;
  #prevState = [];
  #pending = false; // hay un reconcile agendado o corriendo
  #unlockRaf = 0;

  #listening = false;
  #windowTimer = null;

  init() {
    const boot = readBootstrap();
    window.FDV = window.FDV || {};
    window.FDV.gifts = window.FDV.gifts || {};
    Object.assign(window.FDV.gifts, {
      state: [],
      currency: boot.currency || 'MXN',
      debug: window.FDV.gifts.debug || boot.debug === true,
      reconcile: () => this.#scheduleReconcile(),
      reload: () => this.#load(false),
      evaluate: (cart) => evaluate(this.#offers, cart),
      merchSubtotal,
    });
    this.#load(true);
  }

  /**
   * (Re)lee las ofertas del <script id="fdv-gift-offers"> y arranca. Idempotente.
   * @param {boolean} initial - true = carga en el page load (gatea el primer
   *   reconcile por carrito no-vacío); false = `reload()` explícito (siempre reconcilia).
   */
  #load(initial) {
    this.#offers = readOffers();
    window.FDV.gifts.offers = this.#offers;
    const boot = readBootstrap();
    this.#watchWindows();

    if (!this.#offers.length) {
      log('sin ofertas activas — motor en reposo');
      // Si quedan regalos huérfanos en el carrito (una campaña terminó), un
      // reconcile con 0 ofertas los retira. No engancha listeners.
      if (boot.hasGiftLines) this.#scheduleReconcile();
      return;
    }
    log('ofertas activas:', this.#offers.length);

    if (!this.#listening) {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
      // También al aplicar/quitar un código de descuento desde el drawer / la
      // página /cart (`<cart-discount-component>` nativo) → reconciliar por si
      // hay ofertas con `trigger_type: discount_code` y para mantener
      // `fdv-offer-state` fresco.
      document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
      this.#listening = true;
    }

    if (!initial) {
      this.#scheduleReconcile();
      return;
    }
    // Reconcile inicial: solo si el carrito no está vacío (evita fetch en cada carga)...
    if ((boot.cartItemCount ?? 0) > 0) this.#scheduleReconcile();
    // ...o si un url_param de la landing puede desbloquear con carrito vacío.
    else if (this.#offers.some((o) => (o.triggerType === 'cart_subtotal' && o.thresholdCents === 0 &&
      offerActive(o)) ||
      (o.triggerType === 'url_param' && urlParamActive(o)))) this.#scheduleReconcile();
    // Carrito vacío: publica una línea base (todo bloqueado) SIN pegarle a /cart.js,
    // para sembrar #prevState. Sin esto, el primer cambio de carrito (p. ej. agregar
    // la vela de un par `product_in_cart`) sería el primer publish → `#emitTransitions`
    // lo trata como "primer render" y NO dispara `fdv:gift:unlocked` (ni el toast ni
    // el confeti). Ver `#emitTransitions`.
    else this.#publishBaseline();
  }

  /** Activar/cerrar también en pestañas abiertas; revalidar al volver del fondo. */
  #watchWindows() {
    clearTimeout(this.#windowTimer);
    const now = Date.now() / 1000;
    const boundaries = this.#offers.flatMap((o) => [o.startAt, o.publicEndAt, o.endAt]).filter((t) => t > now);
    if (boundaries.length) {
      this.#windowTimer = setTimeout(() => {
        this.#scheduleReconcile();
        this.#watchWindows();
      }, Math.min(2147483647, Math.max(1, Math.ceil((Math.min(...boundaries) - now) * 1000))));
    }
    document.removeEventListener('visibilitychange', this.#onVisible);
    document.addEventListener('visibilitychange', this.#onVisible);
    window.removeEventListener('pageshow', this.#onVisible);
    window.addEventListener('pageshow', this.#onVisible);
  }

  #onVisible = () => {
    if (document.visibilityState === 'hidden') return;
    this.#scheduleReconcile();
    this.#watchWindows();
  };

  /** Estado base contra un carrito vacío sintético (sin red). Siembra `#prevState`. */
  #publishBaseline() {
    const emptyCart = {
      items: [],
      items_subtotal_price: 0,
      total_price: 0,
      item_count: 0,
      currency: window.FDV?.gifts?.currency,
      cart_level_discount_applications: [],
    };
    this.#publish(evaluate(this.#offers, emptyCart), emptyCart);
  }

  #onCartUpdate = (event) => {
    if (this.#ownEvent) return;
    // Bloquea el carrito YA (antes de esperar la promesa de Horizon). Ver #lockCart.
    this.#lockCart();
    const after = () => this.#scheduleReconcile();
    if (event?.promise?.then) event.promise.then(after).catch(after);
    else after();
  };

  #scheduleReconcile() {
    this.#pending = true;
    this.#lockCart();
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#reconcile(), DEBOUNCE_MS);
  }

  /**
   * Bloquea los controles de las líneas del carrito mientras el motor reconcilia.
   *
   * Los botones "quitar" / los steppers de Horizon llevan un **índice de línea
   * posicional** horneado por Liquid (`on:click="/onLineItemRemove/{{ item.index
   * | plus: 1 }}"`). Cuando el motor agrega o quita una línea de regalo, todas
   * las posiciones por debajo se corren y el DOM tarda un instante (un
   * `morphSection` asíncrono) en re-renderizar con los índices nuevos. Si el
   * cliente pulsa "quitar" en esa ventana, el índice viejo apunta a otra línea
   * —o se sale de rango (→ `/cart/change.js` responde 422 "línea no válida")—
   * y se borra el ítem equivocado o no se borra nada. Con regalos en el carrito
   * (líneas que el motor añade al TOPE) esto pasa aún pulsando despacio.
   *
   * La clase va en `<html>` (no en `cart-items-component`, que el `morphSection`
   * de Horizon re-sincroniza y le quitaría la clase a media reconciliación) y es
   * independiente de la `cart-items-disabled` de Horizon (que Horizon libera en
   * su propio `finally`, antes de que el motor termine). Ver
   * `snippets/fdv-gift-engine.liquid` para el CSS. Se libera en `#scheduleUnlock`.
   */
  #lockCart() {
    if (this.#unlockRaf) {
      cancelAnimationFrame(this.#unlockRaf);
      this.#unlockRaf = 0;
    }
    document.documentElement.classList.add('fdv-cart-reconciling');
  }

  #unlockCart() {
    document.documentElement.classList.remove('fdv-cart-reconciling');
  }

  /**
   * Libera el lock tras dejar terminar el morphSection de Horizon (dos frames),
   * con un `setTimeout` de respaldo por si el rAF está congelado (pestaña en
   * segundo plano) — el lock nunca debe quedarse pegado.
   */
  #scheduleUnlock() {
    if (this.#unlockRaf) cancelAnimationFrame(this.#unlockRaf);
    const done = () => {
      this.#unlockRaf = 0;
      if (!this.#pending && !this.#running) this.#unlockCart();
    };
    this.#unlockRaf = requestAnimationFrame(() => {
      this.#unlockRaf = requestAnimationFrame(done);
    });
    setTimeout(done, 150);
  }

  async #reconcile() {
    this.#pending = false;
    if (this.#running) {
      this.#rerunQueued = true;
      return;
    }
    this.#running = true;
    try {
      // Serializa también entre pestañas del mismo origen para evitar altas dobles.
      if (window.navigator?.locks?.request) {
        await window.navigator.locks.request('fdv-gifts-cart', () => this.#reconcileOnce());
      } else {
        await this.#reconcileOnce();
      }
    } catch (e) {
      warn('reconcile falló (el carrito nativo sigue intacto):', e);
    } finally {
      this.#running = false;
      if (this.#rerunQueued) {
        this.#rerunQueued = false;
        this.#scheduleReconcile();
      } else {
        // Ya consistente (líneas + índices re-horneados por el morph) → liberar.
        this.#scheduleUnlock();
      }
    }
  }

  async #reconcileOnce() {
    const cart = await (await fetch(`${themeRoute('cart_url')}.js`, { headers: { Accept: 'application/json' } })).json();
    let state = evaluate(this.#offers, cart);

    // --- construir el mapa de regalos deseados ---
    const desired = new Map(); // variantId -> { qty, label, offerId, mechanism }
    let blocked = false;
    for (const s of state) {
      if (blocked) continue;
      if (s.status === 'unlocked' && s.giftVariantId && s.mechanism !== 'free_shipping_message') {
        const qty = Math.max(0, s.giftQty || 0);
        if (qty > 0) {
          const prev = desired.get(s.giftVariantId);
          if (!prev || qty > prev.qty) {
            desired.set(s.giftVariantId, { qty, label: s.giftLineLabel, offerId: s.id, mechanism: s.mechanism });
          }
        }
      }
      if (s.status === 'unlocked' && !s.stackable) blocked = true;
    }

    // --- universo controlado = regalos de ofertas + líneas con _gift en el carrito ---
    const controlled = new Set(this.#offers.map((o) => o.giftVariantId).filter(Boolean));
    for (const it of cart.items) if (isGiftLine(it)) controlled.add(it.id);

    // --- diff ---
    const removeOps = [];
    const addOps = [];
    const changeOps = [];
    for (const vid of controlled) {
      const giftLines = cart.items.filter((it) => it.id === vid && isGiftLine(it));
      const giftLine = giftLines[0];
      for (const duplicate of giftLines.slice(1)) removeOps.push({ id: duplicate.key, quantity: 0 });
      const want = desired.get(vid);
      const wantQty = want ? want.qty : 0;
      const haveQty = giftLine ? giftLine.quantity : 0;

      if (wantQty === 0 && giftLine) {
        removeOps.push({ id: giftLine.key, quantity: 0 });
      } else if (wantQty > 0 && !giftLine) {
        addOps.push({
          id: vid,
          quantity: wantQty,
          properties: { [GIFT_PROP]: want.offerId, [GIFT_LABEL_PROP]: want.label },
        });
      } else if (wantQty > 0 && giftLine && haveQty !== wantQty) {
        changeOps.push({ id: giftLine.key, quantity: wantQty });
      }
    }

    // --- barrido de líneas de regalo huérfanas ---------------------------------
    // Una línea de una variante de regalo `zero_price` SIN `properties._gift` =
    // el cliente la agregó por su cuenta (página del producto a $0, `/cart/add`,
    // un enlace viejo), no el motor. Esas variantes están dedicadas a $0 — nadie
    // las compra a propósito — así que se retiran SIEMPRE, aunque no tengan la
    // property. Si la oferta califica, el diff de arriba ya encoló el alta de la
    // línea "de verdad" (con `_gift`/`_gift_label` → patch #26 la bloquea); si no
    // califica, la línea desaparece. Devuelve el comportamiento que tenía
    // `syncGiftsFromCart` de Impulse con su whitelist de variant IDs.
    //
    // NO cubre `auto_discount` (ahí la variante de regalo es un producto real y
    // vendible — borrarla a ciegas eliminaría algo que el cliente sí quiere
    // comprar; esa mecánica se apoya en `_gift` + las condiciones del descuento
    // nativo, que Shopify hace cumplir en el checkout).
    const zeroPriceGiftVariants = new Set(
      this.#offers.filter((o) => o.mechanism === 'zero_price' && o.giftVariantId).map((o) => o.giftVariantId)
    );
    for (const it of cart.items) {
      if (!isGiftLine(it) && zeroPriceGiftVariants.has(it.id)) {
        removeOps.push({ id: it.key, quantity: 0 });
      }
    }

    const ops = [...removeOps, ...changeOps, ...addOps];
    if (ops.length === 0) {
      // El motor no toca el carrito → los índices de línea del DOM ya son los
      // que dejó el morph de Horizon. Liberar el lock de inmediato (no esperar
      // los 2 frames de `#scheduleUnlock`).
      if (!this.#pending) this.#unlockCart();
      this.#publish(state, cart);
      return;
    }
    if (ops.length > MAX_OPS_PER_CYCLE) {
      warn(`demasiadas ops (${ops.length}) — abortando por seguridad`, ops);
      return;
    }

    log('ops:', ops);

    // --- ejecutar en el mínimo de requests posible ---
    // Antes: N `/cart/change.js` + N `/cart/add.js` + 1 `/cart/update.js` (serial).
    // Ahora: 1 `/cart/add.js` con todos los items (si hay altas) + 1 `/cart/update.js`
    // que aplica las bajas/cambios en un `updates` por line-key Y devuelve el carrito
    // autoritativo + las secciones para el morph. Quitar un regalo = 1 sola request.
    const sectionIds = cartSectionIds();
    const sectionParams = { sections: sectionIds.join(','), sections_url: location.pathname };

    if (addOps.length) {
      try {
        await postJSON(`${themeRoute('cart_add_url')}`, {
          items: addOps.map((op) => ({ id: op.id, quantity: op.quantity, properties: op.properties })),
        });
        for (const op of addOps) {
          window.FDV?.track?.('fdv_gift_added_to_cart', {
            offer_id: op.properties[GIFT_PROP],
            gift_variant_id: op.id,
            qty: op.quantity,
          });
        }
      } catch (e) {
        // p. ej. una variante de regalo sin stock (inventory policy DENY). No
        // bloqueamos las bajas ni el refresco de secciones que siguen.
        warn('alta de regalo(s) falló:', e);
      }
    }

    const updates = {};
    for (const op of removeOps) updates[op.id] = 0;
    for (const op of changeOps) updates[op.id] = op.quantity;

    const finalCart = await postJSON(`${themeRoute('cart_update_url')}`, { updates, ...sectionParams });

    state = evaluate(this.#offers, finalCart);
    const removedBad = this.#annotateAutoDiscount(state, finalCart);
    this.#dispatchCartUpdate(finalCart);
    this.#publish(state, finalCart);
    // Si se retiró un regalo de auto_discount fallido, reconciliar de nuevo
    // para que el DOM y el estado reflejen la remoción.
    if (removedBad) this.#scheduleReconcile();

    // --- limpiar url_param si su oferta ya no está desbloqueada y no hay regalo ---
    for (const s of state) {
      if (s._offer.triggerType === 'url_param' && s.status !== 'unlocked') clearUrlParam(s._offer);
    }
  }

  /**
   * Marca discountApplied para las ofertas auto_discount y retira el regalo si no aplicó.
   * @returns {boolean} true si retiró algún regalo.
   */
  #annotateAutoDiscount(state, cart) {
    let removed = false;
    for (const s of state) {
      if (s.mechanism !== 'auto_discount' || s.status !== 'unlocked' || !s.giftVariantId) continue;
      const line = cart.items.find((it) => it.id === s.giftVariantId && isGiftLine(it));
      if (!line) continue;
      const porCodigo = s._offer?.triggerType === 'discount_code';
      // Por código: el regalo debe quedar a $0. Cualquier otro descuento sobre la línea (p. ej. BIENVENIDA10) no cuenta.
      const applied = porCodigo
        ? (line.final_line_price ?? line.line_price) <= 0
        : (line.final_line_price ?? line.line_price) <= 0 ||
          (line.line_level_discount_allocations || []).some(
            (a) => !s.autoDiscountTitle || (a.discount_application?.title || '').toUpperCase() === s.autoDiscountTitle
          );
      s.discountApplied = applied;
      if (!applied && porCodigo) {
        // Código inválido, ya usado o con umbral distinto al de Shopify: se retira en silencio y no se reintenta con lo mismo.
        markCodeFailed(s._offer, cart);
        removed = true;
        warn(`regalo por código no quedó a $0 para "${s.internalName}" — retirado`);
        postJSON(`${themeRoute('cart_change_url')}`, { id: line.key, quantity: 0 }).catch(() => {});
        window.FDV?.track?.('fdv_gift_error', { offer_id: s.id, reason: 'codigo_sin_descuento' });
      } else if (!applied) {
        s.error = 'auto_discount_no_aplicado';
        warn(`auto_discount no aplicó para "${s.internalName}" — retirando el regalo`);
        removed = true;
        postJSON(`${themeRoute('cart_change_url')}`, { id: line.key, quantity: 0 }).catch(() => {});
        window.FDV?.track?.('fdv_gift_error', { offer_id: s.id, auto_discount_title: s.autoDiscountTitle });
      }
    }
    return removed;
  }

  #dispatchCartUpdate(cart) {
    const sections = cart.sections || {};
    // El validador de Standard Events exige `lines` con >=1 elemento y quantity > 0.
    // Reportamos el carrito resultante (los listeners de Horizon usan `detail.sections`,
    // no el contenido de `lines`).
    const lines = (cart.items || [])
      .filter((i) => i.quantity > 0)
      .map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
    if (!lines.length) {
      // Carrito vacío tras la reconciliación (p. ej. quitaste el último producto
      // pagado y solo quedaba el regalo emparejado, que el motor retira). El
      // `CartLinesUpdateEvent` exige `lines` no vacío → no podemos despacharlo,
      // así que `component-cart-items` nunca morfea y la línea de regalo ya
      // borrada en el server sigue pintada hasta el siguiente cambio de carrito.
      // Morfeamos las secciones del carrito nosotros para reflejar el vacío.
      for (const [id, html] of Object.entries(sections)) {
        if (typeof html === 'string') {
          try {
            morphSection(id, html);
          } catch (e) {
            warn('morphSection (carrito vacío) falló para', id, e);
          }
        }
      }
      // El ícono del carrito (contador burbuja) también escucha `cartLinesUpdate`
      // → sin evento se queda en la cuenta vieja. Lo ponemos a 0 a mano.
      for (const icon of document.querySelectorAll('cart-icon')) {
        try {
          icon.renderCartBubble?.(0);
        } catch {
          /* noop */
        }
      }
      return;
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#ownEvent = true;
    try {
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines,
          promise: deferred.promise,
        })
      );
    } finally {
      // dejar pasar el turno de microtareas antes de volver a escuchar
      Promise.resolve().then(() => {
        this.#ownEvent = false;
      });
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections, source: 'fdv-gift-engine', didError: false },
    });
  }

  #publish(state, cart) {
    window.FDV.gifts.state = state;
    window.FDV.gifts.cart = cart;
    document.dispatchEvent(new CustomEvent('fdv:gifts:state', { detail: { state, cart } }));
    this.#emitTransitions(this.#prevState, state, cart);
    this.#prevState = state.map((s) => ({ id: s.id, status: s.status }));
  }

  #emitTransitions(prev, next, cart) {
    const track = window.FDV?.track;
    const prevById = new Map(prev.map((s) => [s.id, s.status]));
    for (const s of next) {
      const was = prevById.get(s.id);
      if (was === undefined) continue; // primer render, no es transición
      if (was !== 'unlocked' && s.status === 'unlocked') {
        // Evento DOM para las superficies de UI (celebración, etc.).
        document.dispatchEvent(new CustomEvent('fdv:gift:unlocked', { detail: { offer: s, cart } }));
        track?.('fdv_gift_unlocked', {
          offer_id: s.id,
          trigger_type: s._offer.triggerType,
          gift_variant_id: s.giftVariantId,
          mechanism: s.mechanism,
          threshold: s.threshold,
          cart_subtotal: cart.items_subtotal_price,
        });
        if (s._offer.triggerType === 'discount_code' || s._offer.triggerType === 'url_param') {
          track?.('fdv_promo_claimed', {
            offer_id: s.id,
            code: s._offer.discountCode || null,
            param: s._offer.urlParam || null,
          });
        }
      } else if (was === 'unlocked' && s.status !== 'unlocked') {
        document.dispatchEvent(new CustomEvent('fdv:gift:removed', { detail: { offer: s, cart } }));
        track?.('fdv_gift_removed', {
          offer_id: s.id,
          gift_variant_id: s.giftVariantId,
          cart_subtotal: cart.items_subtotal_price,
        });
      }
    }
  }
}

function themeRoute(key) {
  const r = window.Theme?.routes || {};
  const map = {
    cart_url: r.cart_url || '/cart',
    cart_add_url: r.cart_add_url || '/cart/add.js',
    cart_change_url: r.cart_change_url || '/cart/change.js',
    cart_update_url: r.cart_update_url || '/cart/update.js',
  };
  return map[key];
}

/* -------------------------------------------------------------------- init -- */

const engine = new GiftEngine();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => engine.init(), { once: true });
} else {
  engine.init();
}
